import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { Logger, EndpointService, HttpErrorHandlingService } from '@app/core';
import { DocumentoAnexo, RadicadoCita } from '@app/shared/models/documento.model';
import { Const } from '@app/shared';
import { RadicationYear } from '@app/forms/shared/radication-docs/shared/radication-docs-data.service';

const log = new Logger('FlowService');

export interface DowloadFile {
  path: string;
  name: string;
}

@Injectable()
export class DocumentsRecordsService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService, private hehs: HttpErrorHandlingService,) {
  }


  /**
   *Obtiene los documentos del expediente
   *
   * @param {number} expediente
   * @return {*}  {Observable<any>}
   * @memberof DocumentsRecordsService
   */
  getDocumentExpedient(expediente: number): Observable<any> {
    return new Observable(obs => {
      this.http.get<any>(this.api.get('document_expedient', [expediente])).subscribe(
        stage => {
          obs.next(stage);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }


  /**
   * Servicio para descargar los documentos
   *
   * @param {number} idDocumento
   * @return {*}  {Observable<any>}
   * @memberof DocumentsRecordsService
   */
  dowloadFile(idDocumento: number): Observable<any> {
    return new Observable(obs => {
      this.http
        .skipErrorHandler()
        .get(this.api.get('docsInstance', [idDocumento]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(res.body);
          },
          err => {
            if (err.status !== 401 && err.status !== 403 && err.status !== 500) {
              this.toastr.warning(err.error['message'], 'Fallo de descarga');
            } else {
              this.toastr.error(err.error['message'], 'Fallo de descarga');
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Descargar documento anexado
   *
   * @param {number} idAttached
   * @returns {Observable<DowloadFile>}
   * @memberof DocumentsRecordsService
   */
  getDownloadAttachedDocument(idAttached: number): Observable<DowloadFile> {
    return new Observable(obs => {
      this.http.get<DowloadFile>(this.api.get('downloadAttachedDocument', [idAttached])).subscribe(
        file => {
          obs.next(file);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta de los documentos anexados a una tarea
   *
   * @param {number} idDocument
   * @returns {Observable<DocumentoAnexo>}
   * @memberof DocumentsRecordsService
   */
  getAttachedDocument(idDocument: number): Observable<DocumentoAnexo[]> {
    return new Observable(obs => {
      this.http.get<DocumentoAnexo[]>(this.api.get('attachedDocumentCustom', [idDocument])).subscribe(
        documentsAttached => {
          obs.next(documentsAttached);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Obtiene las series desde el endpoint 'serie'
   *
   * @returns {Observable<any>}
   * @memberof DocumentsRecordsService
   */
  getSeries(): Observable<any> {
    const params = new HttpParams()
      .set('validate_restriction', 'true');
    return new Observable(obs => {
      this.http.get<any>(this.api.get('serie'), { params }).subscribe(
        series => {
          obs.next(series);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Obtiene las subseries filtrando por serie_id
   *
   * @param {number} serieId
   * @returns {Observable<any>}
   * @memberof DocumentsRecordsService
   */
  getSubSeriesBySerieId(serieId: number): Observable<any> {
    const params = new HttpParams()
      .set('serie', serieId.toString())
      .set('validate_restriction', 'true');
    return new Observable(obs => {
      this.http.get<any>(this.api.get('subSerie'), { params }).subscribe(
        subseries => {
          obs.next(subseries);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Obtiene los tipos documentales filtrando por subserie_id
   *
   * @param {number} subSerieId
   * @returns {Observable<any>}
   * @memberof DocumentsRecordsService
   */
  getTypeDocumentsBySubSerieId(subSerieId: number): Observable<any> {
    const params = new HttpParams()
      .set('sub_serie', subSerieId.toString())
      .set('validate_restriction', 'true');

    return new Observable(obs => {
      this.http.get<any>(this.api.get('typeDocument'), { params }).subscribe(
        typeDocuments => {
          obs.next(typeDocuments);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }


  /**
 * Consulta los años de los radicados disponibles en cita
 *
 * @param {number} id_tipo_documental
 * @returns {Observable<RadicadoCita[]>}
 * @memberof RadicationDocsDataService
 */
  getRadicationYears(id_tipo_documental: number): Observable<RadicationYear[]> {
    return new Observable((obs) => {
      this.http.get<RadicationYear[]>(this.api.get("anios_radicados", [id_tipo_documental])).subscribe(
        (res) => {
          obs.next(res);
        },
        (err) =>
          this.hehs.error(err, () => {
            obs.error(err);
          })
      );
    });
  }

  /**
   * Consulta los radicados de cita
   *
   * @param year
   * @param id_tipo_documental
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getRadicationsCita(
    year: number,
    id_tipo_documental: number
  ): Observable<RadicadoCita[]> {
    const formData = new FormData();

    formData.append('year', String(year));
    formData.append('tipo_documental', String(id_tipo_documental));

    return new Observable((obs) => {
      this.http
        .skipErrorHandler()
        .post<RadicadoCita[]>(this.api.get("anios_radicados"), formData, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
    });
  }

  /**
   * Asociar un documento al expediente
   *
   * @returns {Promise<boolean>}
   * @memberof SedesManagementDataService
   * @param idExp
   * @param idDocument
   */
  associateDocument(idExp: number, idDocument: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let parameters = {
        'id_documento': idDocument,
        'id_expediente': idExp
      };
      this.http.post<boolean>(this.api.get('associateDocument'), parameters).subscribe(
        _resp => {
          this.toastr.success('Documento asociado al expediente', 'Operación exitosa');
          resolve(true);
        },
        err => {
          this.toastr.error('No se pudo asociar el documento al expediente', 'Error en la operación');
          reject(err);
        }
      );
    });
  }


} // End class



// WEBPACK FOOTER //
// ./src/app/shared/documents-records/shared/documents-records.data.service.ts